import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';

interface LinkedUser {
  id: string;
  email: string;
  name: string;
}

interface QuickBooksVendor {
  id: string;
  displayName: string;
  companyName?: string;
  givenName?: string;
  familyName?: string;
  printOnCheckName?: string;
  active: boolean;
  balance?: number;
  currencyCode?: string;
  currencyName?: string;
  vendor1099?: boolean;
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  linkedUsers?: LinkedUser[];
}

export const VendorManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();
  const [vendors, setVendors] = useState<QuickBooksVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Determine if we're on the "my-vendors" route (user-specific) or "vendors" route (admin)
  const isMyVendorsRoute = location.pathname.includes('/my-vendors');
  const isAdmin = hasPermission('accounting.admin');

  useEffect(() => {
    fetchVendors();
  }, [page, rowsPerPage, searchQuery, isMyVendorsRoute]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      if (isMyVendorsRoute) {
        // For my-vendors route, fetch only the current user's associated vendors
        const response = await authenticatedFetch(
          `${API_URL}/api/accounting/user/vendors`
        );

        if (!response || !response.ok) {
          if (response?.status === 404) {
            // No vendors associated with this user
            setVendors([]);
            setTotalCount(0);
            setLoading(false);
            return;
          }
          throw new Error('Failed to fetch your vendors');
        }

        const data = await response.json();
        // The user endpoint returns an array of vendors
        const userVendors = data.data?.vendors || [];
        setVendors(userVendors);
        setTotalCount(userVendors.length);
      } else {
        // For admin vendors route, fetch all vendors with pagination
        const params = new URLSearchParams({
          offset: (page * rowsPerPage).toString(),
          limit: rowsPerPage.toString(),
          hasUser: 'true', // Only show vendors with access granted to users
        });

        if (searchQuery) {
          params.append('search', searchQuery);
        }

        const response = await authenticatedFetch(
          `${API_URL}/api/accounting/admin/vendors/list?${params.toString()}`
        );

        if (!response || !response.ok) {
          throw new Error('Failed to fetch vendors');
        }

        const data = await response.json();
        setVendors(data.data?.vendors || []);
        setTotalCount(data.data?.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError(isMyVendorsRoute ? 'Failed to fetch your vendors' : 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number | undefined, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">Vendor Management</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccountBalanceIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {isMyVendorsRoute ? 'My Vendors' : 'QuickBooks Vendors'}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {isMyVendorsRoute
            ? `Vendors associated with your account. Total: ${totalCount}`
            : `Vendors with user access granted. Total: ${totalCount}`
          }
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {!isMyVendorsRoute && (
            <TextField
              size="small"
              placeholder="Search by name, company, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1, maxWidth: 500 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {isMyVendorsRoute && <Box sx={{ flexGrow: 1 }} />}
          {isAdmin && (
            <>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/admin/hub/accounting/users')}
              >
                User Access
              </Button>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => navigate('/admin/hub/accounting/mapping')}
              >
                Invoice-Bill Mappings
              </Button>
            </>
          )}
          <IconButton onClick={fetchVendors} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>

      {/* Vendors Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : vendors.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            {isMyVendorsRoute
              ? 'No vendors are associated with your account. Please contact an administrator to get access.'
              : 'No vendors with user access found matching your search criteria.'
            }
          </Alert>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Company Name</TableCell>
                  {!isMyVendorsRoute && <TableCell>Access Granted to</TableCell>}
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendors.map((vendor, index) => (
                  <TableRow
                    key={vendor.id || `vendor-${index}`}
                    hover
                    onClick={() => navigate(`/admin/hub/accounting/vendors/${vendor.id}`)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {vendor.displayName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {vendor.companyName || '-'}
                    </TableCell>
                    {!isMyVendorsRoute && (
                      <TableCell>
                        {vendor.linkedUsers && vendor.linkedUsers.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {vendor.linkedUsers.map((user) => (
                              <Chip
                                key={user.id}
                                label={user.email}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No users
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      {formatCurrency(vendor.balance, vendor.currencyCode)}
                    </TableCell>
                    <TableCell>
                      {vendor.currencyCode || 'USD'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={vendor.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={vendor.active ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!isMyVendorsRoute && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </>
        )}
      </TableContainer>

      {/* Info Box */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          {isMyVendorsRoute ? 'My Vendors Information' : 'Vendor Management Information'}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {isMyVendorsRoute
            ? 'This page displays QuickBooks vendors associated with your account. Click on any vendor row to view detailed information, invoices, and bills.'
            : 'This page displays QuickBooks vendors that have user access granted. Click on any vendor row to view detailed information.'
          }
        </Typography>
        {isAdmin && (
          <Typography variant="body2" color="text.secondary">
            To manage user-vendor associations for subsidiary accounting access, visit the{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/hub/accounting/users')}
            >
              User Access Control
            </Link>{' '}
            page.
          </Typography>
        )}
      </Paper>
    </Container>
  );
};